import { Button, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { ErrorLogger } from "../../../utils/Logger";
import pageTextConfig from "../../../assets/configs/Add New Gig Page/ListingDetailsPage/textConfig";
import tipsIcon from "../../../assets/images/AddNewGigPage/tips_icon_blue.png";
import gigTitleHintImage from "../../../assets/images/AddNewGigPage/gig_title_hint.png";
import "./ListingDetailsPage.scss";
import { useEffect, useRef, useState } from "react";
import GGDropdownInput from "../../../components/CommonComponents/GGInputComponents/GGDropdownInput";
import AddSpace from "../../../components/UtilityComponents/AddSpace/AddSpace";
import { PriceTableRow, PriceTableRowFormInterface } from "../../../components/PageComponents/AddNewGigPageComponents/PriceTableRow";
import SnackBar, { SnackTypes } from "../../../components/UtilityComponents/SnackBar/SnackBar";
import { SubcategoryAPIHandler } from "../../../services/NetworkClient/BackendAPIs/SubcategoryAPIhandler";
import { CategoryAPIHandler } from "../../../services/NetworkClient/BackendAPIs/CategoryAPIHandler";
import UploadImagesSectionDraggable from "../../../utils/UploadImagesSectionDraggable";
import Footer from "../../../components/CommonComponents/Footer/Footer";

export interface GigPortFolioImage {
  remoteUrl?: string,
  assetId?: string,
  localUrl?: string,
  file?: File
}

export interface GigFornDataInterface {
  title: string,
  description: string,
  categoryName: string | undefined,
  categoryId: string | undefined,
  subcategoryName: string | undefined,
  subcategoryId: string | undefined,
  isListed: boolean,
  images: GigPortFolioImage[],
  priceTable: PriceTableRowFormInterface[]
}

interface ListingdetailsPageProps {
  setCurrentPage: any;
  savedFormData: GigFornDataInterface;
  saveGigFormData: Function;
}

const ListingdetailsPage = ({ setCurrentPage, savedFormData, saveGigFormData }: ListingdetailsPageProps) => {

  // sub-components which are non reusable and only limited to this page
  const ListingDetailsMainContainer = () => {

    // states for all different form components
    const [gigFormData, setGigFormData] = useState<GigFornDataInterface>(savedFormData)

    // Dropdown List
    const [categoryMap, setCategoryMap] = useState<any>({})                 // {key: category name, value: ID}
    const [subcategoryMap, setSubcategoryMap] = useState<any>({})

    // Snackbar based status handling 
    const snackBarRef = useRef<{ showSnackbar(): any }>(null)
    const [snackBarParams, setSnackBarParams] = useState({
      message: "",
      state: SnackTypes.error
    });

    const showSnackbar = (message: string, state: SnackTypes) => {
      setSnackBarParams({ message: message, state: state })
      snackBarRef.current?.showSnackbar()
    }

    const handleSubmit = () => {

      // apply necessary trims and then apply validations and post processing
      const trimmedData = {
        ...gigFormData,
        title: gigFormData.title.trim(),
        description: gigFormData.description.trim(),
        priceTable: gigFormData.priceTable.map(rowData => {
          return {...rowData, description: rowData.description.trim()}
      })
      }
      setGigFormData(trimmedData)
      
      if (validateGigFormData(trimmedData)) {
        saveGigFormData(trimmedData)
        setCurrentPage((currentPage: number) => (currentPage + 1))
      }
    }

    // =========== Form Data Validators  =================
    const validateGigFormData = (formData: GigFornDataInterface): boolean =>  {
      // mandatory checks
      if (formData.title.length === 0) {
        showSnackbar("Gig Title is required", SnackTypes.error)
        return false;
      } else if(formData.categoryName === undefined || formData.categoryName.length === 0) {
        showSnackbar("Gig Category is required", SnackTypes.error)
        return false;
      } else if(formData.subcategoryName === undefined || formData.subcategoryName.length === 0) {
        showSnackbar("Gig subcategory is required", SnackTypes.error)
        return false;
      } else if(formData.description.length === 0) {
        showSnackbar("Gig description is required", SnackTypes.error)
        return false;
      } 

      // precision validations
      if (formData.title.length < 15) {
        showSnackbar("Gig title needs to have at least 15 characters", SnackTypes.error)
        return false;
      } else if (formData.title.length > 200) {
        showSnackbar("Gig title can have at max 200 characters", SnackTypes.error)
        return false;
      } else if(formData.description.length < 100) {
        showSnackbar("Gig description needs to have at least 100 characters", SnackTypes.error)
        return false;
      } else if(formData.description.length > 4000) {
        showSnackbar("Gig description can have at max 4000 characters", SnackTypes.error)
        return false;
      } else if(formData.priceTable.length === 0) {
        showSnackbar("At least one pricing range has to be set up", SnackTypes.error)
        return false;
      } else if(formData.priceTable.length > 0) {
        // validate pricetable 
        for(let i = 0; i< formData.priceTable.length; i++) {
          if (validatePriceTableRow(formData.priceTable, i) === false) {
            return false;
          }
        }
      } 

      // images validation
      if(formData.images.length < 3) {
        showSnackbar("Gig portfolio needs a minimum of 3 images", SnackTypes.error)
        return false;
      } else if(formData.images.length > 10) {
        showSnackbar("Gig portfolio can have a maximum of 10 images", SnackTypes.error)
        return false;
      }
      
      return true
    }

    const validatePriceTableRow = (priceTable: PriceTableRowFormInterface[], rowIndex: number): boolean => {
      if(priceTable[rowIndex].minPrice === undefined ) {
        showSnackbar("Mininum Price is required in price table row " + (rowIndex + 1) + ".", SnackTypes.error)
        return false;
      } else if (priceTable[rowIndex].maxPrice === undefined || priceTable[rowIndex].maxPrice === 0) {
        showSnackbar("Maximum Price is required in price table row " + (rowIndex + 1) + ".", SnackTypes.error)
        return false;
      } else if(priceTable[rowIndex].duration === undefined || priceTable[rowIndex].duration === 0) {
        showSnackbar("Duration is required in price table row " + (rowIndex + 1) + ".", SnackTypes.error)
        return false;
      } else if(priceTable[rowIndex].description.length === 0) {
        showSnackbar("Description is required in price table row " + (rowIndex + 1) + ".", SnackTypes.error)
        return false;
      } else if(priceTable[rowIndex].description.length < 50) {
        showSnackbar("Description in price table row " + (rowIndex + 1) + " should be a minimum of 50 characters.", SnackTypes.error)
        return false;
      } else if(priceTable[rowIndex].description.length > 500) {
        showSnackbar("Description in price table row " + (rowIndex + 1) + " exceeds maximum of 500 characters.", SnackTypes.error)
        return false;
      } else {
        let minPrice = priceTable[rowIndex].minPrice
        let maxPrice = priceTable[rowIndex].maxPrice
        if(minPrice !== undefined && maxPrice !== undefined) {
          if(minPrice > maxPrice) {
              showSnackbar("Minimum price cannot be greater than maximum price in price table row " + (rowIndex + 1) + ".", SnackTypes.error)
              return false
          }
        } else {
          showSnackbar("Minimum and maximum price are required in price table row " + (rowIndex + 1) + ".", SnackTypes.error)
          return false
        }
      }
      return true
    }

    function updateGigFormData(fieldName: string, value: any) {
      setGigFormData((prevFormData) => ({
        ...prevFormData,
        [fieldName]: value,
      }));
    }

    // priceTableUpdations
    const updatePriceTableRow = (event: any, rowIndex: number) => {
      const fieldName = event.target.name
      let value = event.target.value
      if (fieldName === 'minPrice' || fieldName === 'maxPrice' || fieldName === 'duration') {
        if(value === undefined || value === '') 
          value = 0
         value = parseInt(value)
      }

      const newRowInfo = {
        ...gigFormData.priceTable[rowIndex],
        [fieldName]: value,
      }
      const newTable = gigFormData.priceTable.map((row, idx) => (idx === rowIndex ? newRowInfo : row));

      updateGigFormData('priceTable', newTable)
    }

    const deletePriceTableRow = (rowIndex: number) => {
      const newTable = [...gigFormData.priceTable]
      newTable.splice(rowIndex, 1)
      updateGigFormData('priceTable', newTable)
    }

    const fetchSubcategoriesForCategoryId = (categoryId: string) => {
      SubcategoryAPIHandler.shared.getSubcategoriesForCategory(categoryId)
      .then(response => {
        const subcategoryMapUtil:any = {}
        response.subcategories.forEach((item, _) => subcategoryMapUtil[item.name] = item.id)
        setSubcategoryMap(subcategoryMapUtil)
      })
      .catch((err) => { ErrorLogger.log(err, 'ListingDetailsPage.fetchSubcategories'); })
    }

    useEffect(() => {
      if(gigFormData.categoryId) 
        fetchSubcategoriesForCategoryId(gigFormData.categoryId)
      
    },  
    // eslint-disable-next-line
    [gigFormData.categoryId])

    useEffect(() => {
      CategoryAPIHandler.shared.getAllCategories().then(res => {
        const categoryMapUtil: any = {}
        res.categories.forEach((item, _) => categoryMapUtil[item.title] = item.id)
        setCategoryMap(categoryMapUtil)
      }).catch((err) => { ErrorLogger.log(err, 'ListingDetailsPage.fetchCategories'); })
      if(gigFormData.categoryId) {
        fetchSubcategoriesForCategoryId(gigFormData.categoryId)
      }
      // eslint-disable-next-line
    }, [])

    return (
      <Box className="listing-details-container">
        <Typography variant="h6" mb={"2vh"} fontWeight="600">
          {pageTextConfig.pageTitle}
        </Typography>

        <Box className="listing-details-form-container">
          <Box className='listing-details-topForm'>
            <Box className="form-section" width={{ xs: "100%", md: "70%" }}>
              <Box className="form-subsection">
                <Typography variant="h6" fontWeight={'600'} className='center' mb='10px'>{pageTextConfig.formSection.gigTitle}* </Typography>
                <TextField
                  name="title"
                  value={gigFormData.title}
                  onChange={(e) => updateGigFormData(e.target.name, e.target.value)}
                  variant="outlined"
                  placeholder={pageTextConfig.formSection.gigTitlePlaceHolder}
                  helperText={
                    <Box className='helper-container'>
                      <Box className="helper-text center">
                        <InfoIcon sx={{ marginRight: "10px" }} />
                        <Box>
                          <Typography variant="caption" sx={{ fontStyle: 'italic' }} display={{ xs: "none", md: "flex" }}>
                            {pageTextConfig.formSection.gigTitleHelpTexts[0]}
                          </Typography>
                          <Typography variant="caption" sx={{ fontStyle: 'italic' }} >
                            {pageTextConfig.formSection.gigTitleHelpTexts[1]}
                          </Typography>
                        </Box>
                      </Box>

                      <Box>
                        {gigFormData.title.length}/200
                      </Box>
                    </Box>
                  }
                  inputProps={{ maxLength: 200 }}
                  sx={{ marginBottom: '30px' }}
                  fullWidth
                />
              </Box>

              <Box className="cat-subcat-subsection">
                <Box className="category-subsection">
                  <Typography variant="h6" fontWeight={'600'} className='center' mb='10px'>{pageTextConfig.formSection.categoryTitle}* </Typography>
                  <GGDropdownInput 
                    label={gigFormData.categoryName ?  '' : 'Category'}
                    options={[...Object.keys(categoryMap)]} 
                    stateName={'category'} 
                    setStateFunction={(newData: string) => {
                      if (categoryMap[newData]) updateGigFormData('categoryId', categoryMap[newData]);
                      updateGigFormData('categoryName', newData)
                    }} 
                    preValue={gigFormData.categoryName || ""}  />
                </Box >
                <Box className="subcategory-subsection">
                  <Typography variant="h6" fontWeight={'600'} className='center' mb='10px'>{pageTextConfig.formSection.subcategoryTitle}* </Typography>
                  <GGDropdownInput 
                  label={gigFormData.subcategoryName ?  '' : 'Subcategory'}
                  options={[...Object.keys(subcategoryMap)]} 
                  setStateFunction={(newSubcategoryTitle: any) => { 
                    if(subcategoryMap[newSubcategoryTitle]) updateGigFormData('subcategoryId', subcategoryMap[newSubcategoryTitle])
                    updateGigFormData('subcategoryName', newSubcategoryTitle)
                  }} 
                  disabled={gigFormData.categoryName ? false : true}
                  preValue={gigFormData.subcategoryName || ""} 
                  stateName={'subcategory'} />
                </Box>
              </Box>

              <Box className="description-subsection">
                <Typography variant="h6" fontWeight={'600'} mb='10px' className="center">{pageTextConfig.formSection.gigDescription}* </Typography>
                <TextField
                  name="description"
                  value={gigFormData.description}
                  onChange={(e) => updateGigFormData(e.target.name, e.target.value)}
                  variant="outlined"
                  placeholder={pageTextConfig.formSection.gigDescriptionPlaceholder}
                  helperText={
                    <Box className="helper-container">
                      <Box className="helper-text center">
                        <InfoIcon sx={{ marginRight: "10px" }} />
                        <Box>
                          <i>
                            {pageTextConfig.formSection.gigDescriptionHelpTexts[0]}
                          </i>
                        </Box>
                      </Box>

                      <Box>
                        {gigFormData.description.length}/4000
                      </Box>
                    </Box>
                  }
                  multiline
                  rows={16}
                  inputProps={{ maxLength: 4000 }}
                  sx={{ marginBottom: '30px' }}
                  fullWidth
                />
              </Box>

              <Box className="price-table-container">
                <Typography variant="h6" fontWeight={'600'} mb='10px'>{pageTextConfig.formSection.priceTableTitle}*</Typography>
                <Box className='price-table-rows-container'>
                  {gigFormData.priceTable.map((_, index) => (
                    <PriceTableRow index={index} gigFormData={gigFormData} updatePriceTableRow={updatePriceTableRow} deletePriceTableRow={deletePriceTableRow} />
                  ))}

                </Box>
                <Button
                  className="add-new-priceTableRow-button"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  sx={{ marginBottom: '30px' }}
                  onClick={() => {
                    if (validatePriceTableRow(gigFormData.priceTable, gigFormData.priceTable.length - 1)) {
                      updateGigFormData('priceTable', [...gigFormData.priceTable, {
                        minPrice: 0,
                        maxPrice: 0,
                        duration: 0,
                        durationUnit: "days",
                        description: "",
                      }])
                    }
                  }}
                >
                  {pageTextConfig.formSection.gigPriceTableSection.addNewRowButtonText}
                </Button>
              </Box>

              <Typography className="center" variant="h6" fontWeight={'600'} mb='30px'>{pageTextConfig.formSection.uploadPortFolioTitle}* </Typography>

              <UploadImagesSectionDraggable editMode={true} gigFormData={gigFormData} updateGigFormData={updateGigFormData}/>

            </Box>

            <Box className="help-section" display={{ xs: "none", md: "flex" }}>

              <Box id="#gig-title-hint-image-container">
                <img src={gigTitleHintImage} alt="Gig Title Tip" id="gig-title-hint-image" />
              </Box>

              <AddSpace vSpace={"13vh"} hSpace={"0px"} />

              <Box id="gig-description-hint-container">
                <Box className="tips-box">
                  <img src={tipsIcon} className="tip-icon" alt="Quick Tip" />
                  <Typography variant='body2' fontWeight="1000" className="tip-title">
                    {pageTextConfig.helpSection.descriptionTip.tipHeading}
                  </Typography>

                  <Typography variant='body2' fontWeight="1000" className="tip-title">
                    {pageTextConfig.helpSection.descriptionTip.para1Title}
                  </Typography>
                  <Typography variant='body2' className="tip-text">
                    {pageTextConfig.helpSection.descriptionTip.para1Description}
                  </Typography>

                  <Typography variant='body2' fontWeight="1000" className="tip-title">
                    {pageTextConfig.helpSection.descriptionTip.para2Title}
                  </Typography>

                  <Typography variant='body2' className="tip-text">
                    {pageTextConfig.helpSection.descriptionTip.para2Description}
                  </Typography>

                  <Typography variant='body2' fontWeight="1000" className="tip-title">
                    {pageTextConfig.helpSection.descriptionTip.para3Title}
                  </Typography>

                  <Typography variant='body2' className="tip-text">
                    {pageTextConfig.helpSection.descriptionTip.para3Description}
                  </Typography>
                </Box>
              </Box>

              <Box className="tips-box">
                <img src={tipsIcon} className="tip-icon" alt="Quick Tip" />

                <Typography variant='body2' className="tip-text" sx={{ marginTop: "15px" }}>
                  {pageTextConfig.helpSection.priceTableTip.mainTip}
                </Typography>

                <Typography variant='body2' fontWeight="1000" className="tip-title">
                  {pageTextConfig.helpSection.priceTableTip.noteTitle}
                </Typography>

                <Typography variant='body2' fontWeight="1000" className="tip-title">
                  {pageTextConfig.helpSection.priceTableTip.note1}
                </Typography>


                <Typography variant='body2' fontWeight="1000" className="tip-title">
                  {pageTextConfig.helpSection.priceTableTip.note2}

                </Typography>

              </Box>

              <Box className="tips-box">
                <img src={tipsIcon} className="tip-icon" alt="Quick Tip" />

                <Typography variant='body2' className="tip-text" sx={{ marginTop: "15px" }}>
                  {pageTextConfig.helpSection.portfolioTip.mainTip}
                </Typography>

              </Box>
              {/*  Will think if it is needed in next release
              <Button className="white-button" variant='contained' size='medium' sx={{ width: '200px', marginTop: '0px' }}>
                <Typography fontWeight={'1000'}> {pageTextConfig.helpSection.needHelpButtonText}</Typography>
              </Button> */}
            </Box>
          </Box>

          <Box className="listing-details-nextButton">
            <Button className="add-new-priceTableRow-button" variant='outlined' endIcon={<KeyboardDoubleArrowRightIcon />} size="large" sx={{ width: '200px', marginTop: '30px' }}
              onClick={handleSubmit}
            >Next</Button>
          </Box>
        </Box>
        <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={5000} message={snackBarParams.message} />
      </Box>
    );
  };

  return (
    <Box className="listing-details-page">
      <ListingDetailsMainContainer />
      <Footer />
    </Box>
  );
};

export default ListingdetailsPage;
